import React, { forwardRef, useMemo } from "react";
import { Button, Menu, MenuItem } from "@appsmith/wds";
import { FocusScope } from "@react-aria/focus";
import { useDOMRef } from "@react-spectrum/utils";
import { useListState } from "@react-stately/list";
import { Item } from "@react-stately/collections";
import { MenuTrigger } from "react-aria-components";
import { ToolbarButton } from "./ToolbarButton";
import { ToolbarToggleButton } from "./ToolbarToggleButton";
import { useToolbarButtons } from "./useToolbarButtons";
import {
  buildOverflowEntries,
  routeOverflowMenuAction,
} from "./overflowRouting";
import styles from "./styles.module.css";
import type { ToolbarButtonsItem, ToolbarButtonsProps } from "./types";
import type { DOMRef, CollectionChildren, Key } from "@react-types/shared";

interface ToolbarButtonsInnerProps<T> extends ToolbarButtonsProps<T> {
  children?: CollectionChildren<T>;
}

const _ToolbarButtonsInner = <T extends ToolbarButtonsItem>(
  props: ToolbarButtonsInnerProps<T>,
  ref: DOMRef<HTMLDivElement>,
) => {
  const {
    alignment = "start",
    color = "accent",
    density = "regular",
    excludeFromTabOrder = false,
    isDisabled,
    onAction,
    onMenuItemAction,
    orientation = "horizontal",
    selectedKeys,
    size = "medium",
    variant = "filled",
  } = props;
  const domRef = useDOMRef(ref);
  const state = useListState({
    ...props,
    // `selectedKeys` here drives the opt-in toggle items only; it is not the
    // collection's selection state (the toolbar has no selection mode), so it
    // must not reach useListState.
    selectedKeys: undefined,
    suppressTextValueWarning: true,
  });
  const selectedKeySet = useMemo(
    () => new Set<Key>(selectedKeys ?? []),
    [selectedKeys],
  );
  const { rovingIndex, toolbarButtonsProps, visibleItems } = useToolbarButtons(
    props,
    state,
    domRef,
  );

  const menuChildren = useMemo(
    () => (props.items as ToolbarButtonsItem[]).slice(visibleItems),
    [props.items, visibleItems],
  );
  // See buildOverflowEntries: the mapping lives beside the routing helper so
  // it is unit-testable (the width-based collapse that feeds it cannot be
  // exercised in jsdom).
  const { overflowEntries, overflowRoutes } = useMemo(
    () => buildOverflowEntries(menuChildren),
    [menuChildren],
  );
  const children = useMemo(
    () => [...state.collection].slice(0, visibleItems),
    [state.collection, visibleItems],
  );

  return (
    <FocusScope>
      <div
        className={styles.toolbarButtons}
        data-alignment={alignment}
        data-density={Boolean(density) ? density : undefined}
        data-orientation={orientation}
        ref={domRef}
        {...toolbarButtonsProps}
      >
        {children.map((item, index) => {
          if (Boolean(item.props.isSeparator)) {
            return <div data-separator="" key={item.key} role="separator" />;
          }

          const itemIsDisabled =
            Boolean(state.disabledKeys.has(item.key)) ||
            Boolean(item.props.isDisabled) ||
            isDisabled;
          // role="toolbar" is a single tab stop: only the roving item stays in
          // the tab order, and `excludeFromTabOrder` still wins over it.
          const itemExcludeFromTabOrder =
            excludeFromTabOrder || index !== rovingIndex;
          const menuItems = item.props.menuItems as
            | ToolbarButtonsItem["menuItems"]
            | undefined;

          if (menuItems && menuItems.length > 0) {
            return (
              <MenuTrigger key={item.key}>
                <ToolbarButton
                  color={color}
                  excludeFromTabOrder={itemExcludeFromTabOrder}
                  icon={item.props.icon}
                  iconPosition={item.props.iconPosition}
                  isDisabled={itemIsDisabled}
                  isLoading={item.props.isLoading}
                  item={item}
                  size={Boolean(size) ? size : undefined}
                  state={state}
                  variant={variant}
                />
                <Menu
                  onAction={(menuKey) => onMenuItemAction?.(item.key, menuKey)}
                >
                  {menuItems.map((menuItem) => (
                    <MenuItem
                      id={menuItem.id}
                      isDisabled={menuItem.isDisabled}
                      key={menuItem.id}
                      textValue={menuItem.label}
                    >
                      {menuItem.label}
                    </MenuItem>
                  ))}
                </Menu>
              </MenuTrigger>
            );
          }

          if (Boolean(item.props.isToggle)) {
            return (
              <ToolbarToggleButton
                aria-label={item.props["aria-label"]}
                color={color}
                excludeFromTabOrder={itemExcludeFromTabOrder}
                icon={item.props.icon}
                iconPosition={item.props.iconPosition}
                isDisabled={itemIsDisabled}
                isLoading={item.props.isLoading}
                isSelected={selectedKeySet.has(item.key)}
                item={item}
                key={item.key}
                onChange={() => onAction?.(item.key)}
                size={Boolean(size) ? size : undefined}
                variant={variant}
              />
            );
          }

          return (
            <ToolbarButton
              aria-label={item.props["aria-label"]}
              color={color}
              excludeFromTabOrder={itemExcludeFromTabOrder}
              icon={item.props.icon}
              iconPosition={item.props.iconPosition}
              isDisabled={itemIsDisabled}
              isLoading={item.props.isLoading}
              item={item}
              key={item.key}
              onPress={() => onAction?.(item.key)}
              size={Boolean(size) ? size : undefined}
              state={state}
              variant={variant}
            />
          );
        })}
        {menuChildren?.length > 0 && (
          <MenuTrigger>
            <Button
              color={color}
              data-action-group-menu
              excludeFromTabOrder={
                excludeFromTabOrder || children.length !== rovingIndex
              }
              icon="dots"
              isDisabled={isDisabled}
              variant={variant}
            />
            <Menu
              onAction={(key) =>
                routeOverflowMenuAction(
                  key,
                  overflowRoutes,
                  onAction,
                  onMenuItemAction,
                )
              }
            >
              {overflowEntries.map((entry) => (
                <MenuItem
                  id={entry.id}
                  isDisabled={entry.isDisabled}
                  key={entry.id}
                  textValue={entry.label}
                >
                  {entry.label}
                </MenuItem>
              ))}
            </Menu>
          </MenuTrigger>
        )}
      </div>
    </FocusScope>
  );
};

const ToolbarButtonsInner = forwardRef(_ToolbarButtonsInner);

const _ToolbarButtons = <T extends ToolbarButtonsItem>(
  props: Omit<ToolbarButtonsProps<T>, "children">,
  ref: DOMRef<HTMLDivElement>,
) => {
  const { items, ...rest } = props;

  return (
    <ToolbarButtonsInner items={items} {...rest} ref={ref}>
      {(item) => <Item {...item}>{item.label}</Item>}
    </ToolbarButtonsInner>
  );
};

export const ToolbarButtons = forwardRef(_ToolbarButtons);
